import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate } from "react-router";
import BootStrapSpinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Button, Container, InputGroup } from "react-bootstrap";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import {
  emptyString,
  surveySystem,
  loginConstants,
  paramsConstants,
  inputFieldTypes,
  viewPathConstants,
  toastTypes,
  textStrings,
  PRIVACY_POLICY_LINK,
  PASSWORD_MIN_LENGTH,
  PASSWORD_VALIDATION_PATTERN,
  passwordValidationMessages,
  DEFAULT_PASSWORD_CHECK_VALIDATION,
  emailVerificationViewConstants,
  STYLES,
  screenSize,
  phoneConstants,
} from "../../constants";
import {
  useSpinner,
  customToast,
  getClassName,
  getConditionalValues,
  getPasswordValidationState,
  PasswordValidationChecklist,
} from "../../shared";
import { emailVerificationActions, loginActions } from "../../redux/actions";

const { TEXT, PASSWORD, CHECKBOX } = inputFieldTypes;
const { IS_INVALID, BORDER_DANGER, TEXT_SUCCESS } = STYLES;
const { SURVEY_SYSTEM, INVALID_TOKEN } = surveySystem;
const {
  INVITATION_EXPIRED,
  SUPPORT_EMAIL,
  INVITATION_EXPIRED_CONTACT,
  ACCOUNT_SETUP,
  EMAIL_LABEL,
  MOBILE_PHONE_LABEL,
  PASSWORD_LABEL,
  CONFIRM_PASSWORD_LABEL,
  PRIVACY_POLICY,
  PRIVACY_AGREEMENT_PREFIX,
  SETUP_PASSWORD_HINT,
  getInvitationExpiredBody,
  getWelcomeInviteLine,
} = emailVerificationViewConstants;

const EmailVerification = (props) => {
  const navigate = useNavigate();

  const {
    verifyUser,
    verifyEmail,
    updateUserPassword,
    resetUpdatedPasswordMsg,
  } = props;

  const [encryptedParams] = useSearchParams();

  const [windowSize, setWindowSize] = useState(() => window.innerWidth);
  const [data, setData] = useState({});
  const [checkValidation, setCheckValidation] = useState(
    DEFAULT_PASSWORD_CHECK_VALIDATION,
  );
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: emptyString,
      phone: emptyString,
    },
  });

  const { checkEquality } = checkValidation;

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Handle change in phone, password and confirm password
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === CHECKBOX ? checked : value;

    //If user change password check validation for highlghting labels
    setData((prev) => {
      const updated = { ...prev, [name]: nextValue };

      if (name === loginConstants.TYPE_PASSWORD) {
        setCheckValidation(
          getPasswordValidationState(value, updated.confirmPassword),
        );
      } else if (name === loginConstants.TYPE_CONFIRM_PASSWORD) {
        setCheckValidation((prevValidation) => ({
          ...prevValidation,
          checkEquality: value ? value === updated.password : false,
        }));
      }

      return updated;
    });
  };

  //Function to submit form
  const submit = () => {
    //Check if password and confirm password are equal esle show toast
    if (checkEquality) {
      const body = {
        phoneNumber: `${phoneConstants.COUNTRY_CODE}${data?.phone}`,
        password: data?.password,
        id: verifyUser?.userId || null,
        token: verifyUser?.token,
        isClarityAllowed: Boolean(data.isClarityAllowed),
      };
      updateUserPassword(body, callBackFunc);
    } else {
      customToast(loginConstants.PASSWORD_NOT_EQUAL, toastTypes.ERROR);
    }
  };

  const callBackFunc = () => {
    reset();
    setData({});
    setCheckValidation(DEFAULT_PASSWORD_CHECK_VALIDATION);
    resetUpdatedPasswordMsg();
  };

  const handleOnBeforeUnload = (_event) => {
    navigate(viewPathConstants.LOGIN, {
      replace: true,
    });
  };

  const renderShowPasswordButton = (name, flag) => {
    return (
      <InputGroup.Text
        id={name}
        className={`${errors[name] && BORDER_DANGER}`}
      >
        <FontAwesomeIcon
          size="sm"
          title={
            showPassword[flag] ? "Hide Password" : "Show Password"
          }
          icon={showPassword[flag] ? faEye : faEyeSlash}
          onClick={() =>
            setShowPassword((prev) => ({
              ...prev,
              [flag]: !prev[flag],
            }))
          }
          className="cursor-pointer"
        />
      </InputGroup.Text>
    );
  };

  // Use effect that creates object from values from params and verifies it
  useEffect(() => {
    const verificationToken = encryptedParams.get(
      paramsConstants.VERIFICATION_TOKEN,
    );
    if (verificationToken) {
      verifyEmail(verificationToken);
    } else if (!verificationToken && !verifyUser?.email) {
      handleOnBeforeUnload();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (verifyUser?.emailConfirm && verifyUser?.phone && !verifyUser?.token) {
      window.location.assign(viewPathConstants.LOGIN);
    }
    // eslint-disable-next-line
  }, [verifyUser]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setWindowSize(window.innerWidth);
    // eslint-disable-next-line
  }, [windowSize]);

  const renderContent = () => {
    if (verifyUser === INVALID_TOKEN) {
      return (
        <div
          className={`bg-white rounded d-flex  ${getClassName(
            windowSize < screenSize.LAYOUT_MOBILE,
            "vh-100",
          )}`}
        >
          <Container className="rounded pb-3 pt-2 my-auto shadow">
            <div className="text-center px-4 pt-2">
              <span className="login-platform-name login-platform-firstName-color pb-3">
                {INVITATION_EXPIRED}
              </span>
              <p className="pt-3">
                {getInvitationExpiredBody(SURVEY_SYSTEM)}
                <br></br>
                {INVITATION_EXPIRED_CONTACT}
                <br></br>
                <strong className="login-platform-firstName-color">
                  {SUPPORT_EMAIL}
                </strong>
              </p>
            </div>
          </Container>
        </div>
      );
    }

    return (
      <div className=" shadow bg-white rounded my-auto">
        <Container className="my-auto rounded px- pb-3 pt-2 ">
          <div className="text-center px-4 pt-2">
            <span className="login-platform-name login-platform-firstName-color">
              {ACCOUNT_SETUP}
            </span>
            <p>
              Welcome {verifyUser?.name}
              <br></br>
              {getWelcomeInviteLine(verifyUser?.brand || SURVEY_SYSTEM)}
              <br></br>
              {SETUP_PASSWORD_HINT}
            </p>
          </div>
          <Form onSubmit={handleSubmit(submit)} className="w-100 px-4">
            <Form.Group controlId="email">
              <Form.Label>{EMAIL_LABEL}</Form.Label>
              <Form.Control
                type="text"
                name="text"
                className="form-control"
                value={verifyUser?.email}
                disabled
              />
            </Form.Group>
            <Form.Group controlId="phone">
              <Form.Label>{MOBILE_PHONE_LABEL}</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  className={`fw-medium ${getClassName(
                    errors.phone,
                    BORDER_DANGER,
                  )}`}
                >
                  {phoneConstants.COUNTRY_DISPLAY}
                </InputGroup.Text>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={data?.phone}
                  minLength={phoneConstants.LENGTH}
                  maxLength={phoneConstants.LENGTH}
                  className={`form-control ${getClassName(
                    errors.phone,
                    IS_INVALID,
                  )}`}
                  {...register("phone", {
                    required: true,
                    pattern: {
                      value: phoneConstants.PATTERN,
                      message: phoneConstants.INVALID_MESSAGE,
                    },
                    onChange: (e) => {
                      if (!isNaN(e.target.value)) handleChange(e);
                    },
                  })}
                  onPaste={(e) => handleChange(e)}
                  autoComplete="off"
                />
              </InputGroup>
              <Form.Text className="text-muted">
                <ul className="ps-3 mb-1">
                  <li
                    className={`${getClassName(
                      data?.phone?.length === phoneConstants.LENGTH,
                      TEXT_SUCCESS,
                    )}`}
                  >
                    <small>{phoneConstants.FORMAT_HINT}</small>
                  </li>
                </ul>
              </Form.Text>
            </Form.Group>
            <Form.Group controlId="password">
              <Form.Label>{PASSWORD_LABEL}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={getConditionalValues(showPassword.new, TEXT, PASSWORD)}
                  name="password"
                  min={PASSWORD_MIN_LENGTH}
                  data-testid="password"
                  {...register("password", {
                    required: true,
                    pattern: {
                      value: PASSWORD_VALIDATION_PATTERN,
                      message: passwordValidationMessages.NOT_VALID,
                    },
                    onChange: (e) => handleChange(e),
                  })}
                  onPaste={(e) => handleChange(e)}
                  className={`form-control ${getClassName(
                    errors.password,
                    IS_INVALID,
                  )}`}
                  autoComplete="new-password"
                />
                {renderShowPasswordButton("password", "new")}
              </InputGroup>
            </Form.Group>
            <Form.Group controlId="confirmPassword" className="mt-2">
              <Form.Label>{CONFIRM_PASSWORD_LABEL}</Form.Label>
              <InputGroup>
                <Form.Control
                  min={PASSWORD_MIN_LENGTH}
                  name="confirmPassword"
                  type={getConditionalValues(
                    showPassword.confirm,
                    TEXT,
                    PASSWORD,
                  )}
                  data-testid="confirm-password"
                  {...register("confirmPassword", {
                    required: true,
                    pattern: {
                      value: PASSWORD_VALIDATION_PATTERN,
                      message: passwordValidationMessages.NOT_VALID,
                    },
                    onChange: (e) => handleChange(e),
                  })}
                  onPaste={(e) => handleChange(e)}
                  className={`form-control ${getClassName(
                    errors.confirmPassword,
                    IS_INVALID,
                  )}`}
                />
                {renderShowPasswordButton("confirmPassword", "confirm")}
              </InputGroup>
              <Form.Text className="text-muted">
                <PasswordValidationChecklist
                  checkValidation={checkValidation}
                  listGroupClassName="my-2 py-2"
                />
              </Form.Text>
            </Form.Group>
            <Form.Group
              controlId="privacyCheck"
              className="my-2 d-flex align-items-center gap-2 d-none"
            >
              <Form.Check
                min={PASSWORD_MIN_LENGTH}
                type={CHECKBOX}
                value={true}
                name="isClarityAllowed"
                data-testid="isClarityAllowed"
                {...register("isClarityAllowed", {
                  required: false,
                  onChange: (e) => handleChange(e),
                })}
                onPaste={(e) => handleChange(e)}
                className="m-0"
                isInvalid={!!errors.isClarityAllowed}
              />
              <Form.Label className="m-0 fw-normal">
                {PRIVACY_AGREEMENT_PREFIX}{" "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={PRIVACY_POLICY_LINK}
                >
                  {PRIVACY_POLICY}
                </a>
              </Form.Label>
            </Form.Group>
            <div className="text-center">
              <Button
                type="submit"
                className=" w-100"
                disabled={spinner(loginConstants.UPDATE_PASSWORD)}
              >
                {textStrings.JOIN_ACCOUNT}
                <span>
                  {spinner(loginConstants.UPDATE_PASSWORD) && (
                    <BootStrapSpinner
                      animation="border"
                      size="sm"
                      className="ms-1"
                    />
                  )}
                </span>
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    );
  };

  const isRenderContent = () => {
    return !(!verifyUser || (verifyUser?.emailConfirm && verifyUser?.phone));
  };

  return (
    <>
      {isRenderContent && (
        <Container
          fluid
          className={`d-flex align-items-center justify-content-center ${getClassName(
            windowSize > screenSize.LAYOUT_DESKTOP,
            "email-confirmation-container",
          )}`}
        >
          {renderContent()}
        </Container>
      )}
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  verifyUser: state.emailVerificationReducer.verifyEmail,
});
const mapDispatchToProps = (dispatch) => ({
  verifyEmail: (body) => dispatch(emailVerificationActions.verifyEmail(body)),
  updateUserPassword: (body, callBackFunc) =>
    dispatch(loginActions.updateUserPassword(body, callBackFunc)),
  resetUpdatedPasswordMsg: () =>
    dispatch(loginActions.resetUpdatedPasswordMsg()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmailVerification);
